/**
 * @file Adaptations to the automatically generated Zod schemas.
 */

import {
  AuthCompanyInvitationSignupBody,
  AuthInvitationConfirmBody,
} from "@boundless/bl-api-client/auth/services.zod"
import { CommonDocumentTypesCreateBody } from "@boundless/bl-api-client/common/services.zod"
import {
  CustomerApplicationsSelectLenderCreateBody,
  CustomerFundingNeedsCreateBody,
  CustomerMajorityOwnerCreateBody,
  CustomerRepositoryUpdateBody,
} from "@boundless/bl-api-client/customer/services.zod"
import { z } from "zod"

import { SSN_REQUIRED_CITIZENSHIP } from "@/lib/constants"
import { SSN_ERROR_MAPPING } from "@/lib/errors"

export const ownershipDetailsFormSchemaBase =
  CustomerMajorityOwnerCreateBody.omit({
    ownerIndex: true,
  })

/**
 * Build the ownership details validation schema with custom SSN requirements.
 *
 * Custom validation logic:
 * - If the owner is **not a foreign citizen** and their `citizenship` belongs
 *   to a country that requires an SSN, then the `ssn` field becomes required.
 * - If the `ssn` field is missing or empty under those conditions, a
 *   `ZodIssue` is added to flag the error.
 *
 * Implementation details:
 * - Uses `z.preprocess` to execute this custom logic **before** the base schema.
 * - Adds the custom issue through `ctx.addIssue`, targeting the `ssn` field.
 *
 * This ensures that the SSN requirement is enforced dynamically based on
 * citizenship and foreign status, while still leveraging the base schema’s
 * validation rules.
 */
export const ownershipDetailsFormSchema = z.preprocess((raw, ctx) => {
  if (typeof raw !== "object" || raw === null) return raw

  const data = raw as Record<string, unknown>
  const { ssn, citizenship, isForeignCitizen } = data

  const isCitizenshipRequired =
    !isForeignCitizen &&
    (citizenship === undefined ||
      SSN_REQUIRED_CITIZENSHIP.includes(citizenship as string))

  const isSsnMissing = typeof ssn !== "string" || ssn.trim() === ""

  if (isCitizenshipRequired && isSsnMissing) {
    const message =
      SSN_ERROR_MAPPING[citizenship as keyof typeof SSN_ERROR_MAPPING] ??
      "Please enter a valid social security number."

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["ssn"],
    })
  }
  return data
}, ownershipDetailsFormSchemaBase)

export const fundingNeedsFormSchema = CustomerFundingNeedsCreateBody.pick({
  capitalAmount: true,
  useOfFunds: true,
})

export const applicationSelectLenderFormSchema =
  CustomerApplicationsSelectLenderCreateBody.pick({
    lender: true,
  })

export const uncategorizedDocumentFormSchema = z.object({
  ...CustomerRepositoryUpdateBody.pick({ type: true }).shape,
  ...CommonDocumentTypesCreateBody.pick({ category: true }).shape,
})

export const invitationFormSchema = z
  .object({
    ...AuthInvitationConfirmBody.shape,
    passwordConfirm: z.string().min(1),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Passwords do not match",
    path: ["passwordConfirm"],
  })
  .refine((data) => !!data.hasAcceptedTerms, {
    message: "Please read and accept the terms and conditions",
    path: ["hasAcceptedTerms"],
  })

/**
 * Same shape as `invitationFormSchema`, plus the name fields.
 *
 * A Company Invitation carries only an email address, so unlike a User
 * Invitation there is no name on file to prefill and disable.
 */
export const companyInvitationFormSchema = z
  .object({
    ...AuthCompanyInvitationSignupBody.shape,
    passwordConfirm: z.string().min(1),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Passwords do not match",
    path: ["passwordConfirm"],
  })
  .refine((data) => !!data.hasAcceptedTerms, {
    message: "Please read and accept the terms and conditions",
    path: ["hasAcceptedTerms"],
  })
